import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "./ProductCard";
import SeoMeta from "@/components/SeoMeta";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SlidersHorizontal, Search, X } from "lucide-react";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import { storefrontCardVariant } from "@/lib/storefrontTheme";
import { loadGarage, saveGarage, garageActive, trimsFor, modelsForMake, yearsFor } from "@/lib/carParts";

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "name", label: "Name: A → Z" },
];

function FilterPanel({ categories, catSlug, setCatSlug, minPrice, setMinPrice, maxPrice, setMaxPrice, sort, setSort, search, setSearch, primary, onApply, garage, setGarage, garageData, config }) {
  const { t } = useTranslation();
  const tree = garageData?.tree || {};
  const makes = Object.keys(tree || {});
  const models = modelsForMake(tree, garage.make);
  const years = yearsFor(tree, garage.make, garage.model);
  const trims = trimsFor(tree, garage.make, garage.model, garage.year);
  const setVeh = (patch) => setGarage((g) => ({ ...g, ...patch }));
  return (
    <div className="space-y-7">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 mb-3">{t("common.search")}</div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products" className="pl-9 rounded-full" data-testid="shop-search" />
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 mb-3">{t("nav.categories")}</div>
        <div className="space-y-1">
          <button onClick={() => setCatSlug("")} data-testid="shop-cat-all" className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${!catSlug ? "font-semibold text-white" : "text-stone-600 hover:bg-stone-100"}`} style={!catSlug ? { background: primary } : {}}>{t("store.allProducts")}</button>
          {categories.filter((c) => !c.parent_id).map((c) => (
            <div key={c.id}>
              <button onClick={() => setCatSlug(c.slug)} data-testid={`shop-cat-${c.slug}`} className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${catSlug === c.slug ? "font-semibold text-white" : "text-stone-600 hover:bg-stone-100"}`} style={catSlug === c.slug ? { background: primary } : {}}>{c.name}</button>
              {categories.filter((ch) => ch.parent_id === c.id).map((ch) => (
                <button key={ch.id} onClick={() => setCatSlug(ch.slug)} data-testid={`shop-cat-${ch.slug}`} className={`w-full text-left pl-6 pr-3 py-1.5 rounded-xl text-sm transition-colors ${catSlug === ch.slug ? "font-semibold text-white" : "text-stone-500 hover:bg-stone-100"}`} style={catSlug === ch.slug ? { background: primary } : {}}>{ch.name}</button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {config.car_parts_active ? (
        <div data-testid="shop-garage">
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 mb-3">Select your vehicle</div>
          <p className="text-[11px] text-stone-500 mb-2">Make, Model, Year, Trim — only parts that fit will show. Universal-fit items always appear.</p>
          <div className="space-y-2">
            <select
              value={garage.make}
              data-testid="shop-garage-make"
              onChange={(e) => setVeh({ make: e.target.value, model: "", year: "", trim: "" })}
              className="w-full rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-700"
            >
              <option value="">Make</option>
              {makes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              value={garage.model}
              data-testid="shop-garage-model"
              disabled={!garage.make}
              onChange={(e) => setVeh({ model: e.target.value, year: "", trim: "" })}
              className="w-full rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-700 disabled:opacity-50"
            >
              <option value="">Model</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              value={garage.year}
              data-testid="shop-garage-year"
              disabled={!garage.model}
              onChange={(e) => setVeh({ year: e.target.value, trim: "" })}
              className="w-full rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-700 disabled:opacity-50"
            >
              <option value="">Year</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select
              value={garage.trim}
              data-testid="shop-garage-trim"
              disabled={!garage.year}
              onChange={(e) => setVeh({ trim: e.target.value })}
              className="w-full rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-700 disabled:opacity-50"
            >
              <option value="">Trim (optional)</option>
              {trims.map((trimName) => <option key={trimName} value={trimName}>{trimName}</option>)}
            </select>
            {garageActive(garage) ? (
              <button type="button" data-testid="shop-garage-clear" className="text-xs font-semibold text-stone-500 underline" onClick={() => setVeh({ make: "", model: "", year: "", trim: "" })}>Clear vehicle</button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div>
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 mb-3">Price range (₹)</div>
        <div className="flex items-center gap-2">
          <Input type="number" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="Min" className="rounded-full font-mono-nums" data-testid="shop-min-price" />
          <span className="text-stone-400">–</span>
          <Input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max" className="rounded-full font-mono-nums" data-testid="shop-max-price" />
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 mb-3">Sort by</div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort" className="w-full rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300">
          {SORTS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
        </select>
      </div>

      {onApply && (
        <Button onClick={onApply} className="w-full btn-store-primary rounded-full h-11" data-testid="shop-apply-mobile">Show results</Button>
      )}
    </div>
  );
}

export default function StorefrontShop({ config }) {
  const { t } = useTranslation();
  const { slug, base, country } = useStoreBase();
  const [searchParams, setSearchParams] = useSearchParams();
  const primary = config.branding?.primary_color || "#15803D";
  const cardVariant = storefrontCardVariant(config);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const featuredOnly = searchParams.get("featured") === "true";
  const tagSlug = searchParams.get("tag") || "";
  const [catSlug, setCatSlug] = useState(searchParams.get("category") || "");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState(() => searchParams.get("q") || searchParams.get("search") || "");
  const urlQ = searchParams.get("q") || searchParams.get("search") || "";
  const [garageData, setGarageData] = useState(null);
  const [garage, setGarage] = useState(() => {
    const fromUrl = {
      year: searchParams.get("year") || "",
      make: searchParams.get("make") || "",
      model: searchParams.get("model") || "",
      trim: searchParams.get("trim") || "",
    };
    if (fromUrl.year || fromUrl.make || fromUrl.model || fromUrl.trim) return fromUrl;
    return loadGarage(slug);
  });

  useEffect(() => {
    setSearch(urlQ);
  }, [urlQ]);

  useEffect(() => {
    api.get(`/store/${slug}/categories`).then(({ data }) => setCategories(data));
  }, [slug]);

  useEffect(() => {
    if (!config.car_parts_active) { setGarageData(null); return; }
    api.get(`/store/${slug}/car-parts/garage`).then(({ data }) => setGarageData(data)).catch(() => setGarageData(null));
  }, [slug, config.car_parts_active]);

  useEffect(() => {
    saveGarage(slug, garage);
  }, [slug, garage]);

  // Keep URL in sync with category selection (shareable links)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (catSlug) next.set("category", catSlug); else next.delete("category");
    if (garage.year) next.set("year", garage.year); else next.delete("year");
    if (garage.make) next.set("make", garage.make); else next.delete("make");
    if (garage.model) next.set("model", garage.model); else next.delete("model");
    if (garage.trim) next.set("trim", garage.trim); else next.delete("trim");
    setSearchParams(next, { replace: true });
  }, [catSlug, garage.year, garage.make, garage.model, garage.trim]);

  // Debounced fetch on any filter change
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams();
      if (catSlug) params.set("category", catSlug);
      if (search.trim()) params.set("search", search.trim());
      if (minPrice !== "") params.set("min_price", minPrice);
      if (maxPrice !== "") params.set("max_price", maxPrice);
      if (sort) params.set("sort", sort);
      if (country) params.set("country", country);
      if (featuredOnly) params.set("featured", "true");
      if (garage.make) params.set("make", garage.make);
      if (garage.model) params.set("model", garage.model);
      if (garage.year) params.set("year", garage.year);
      if (garage.trim) params.set("trim", garage.trim);
      if (tagSlug) params.set("tag", tagSlug);
      api.get(`/store/${slug}/products?${params.toString()}`)
        .then(({ data }) => setProducts(data))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [slug, catSlug, search, minPrice, maxPrice, sort, country, featuredOnly, garage.make, garage.model, garage.year, garage.trim, tagSlug]);

  const activeCat = useMemo(() => categories.find((c) => c.slug === catSlug), [categories, catSlug]);
  const filterProps = { categories, catSlug, setCatSlug, minPrice, setMinPrice, maxPrice, setMaxPrice, sort, setSort, search, setSearch, primary, garage, setGarage, garageData, config };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SeoMeta endpoint={`/seo/${slug}/meta`} fallback={{ title: `Shop · ${config.store_info?.name || slug}` }} />

      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: primary }}>{t("store.shop")}</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold text-stone-900">{activeCat ? activeCat.name : t("store.allProducts")}</h1>
          <p className="text-stone-500 text-sm mt-1">{loading ? t("common.loading") : `${products.length} ${t("products.title")}`}</p>
        </div>
        {/* Mobile filters trigger */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="lg:hidden rounded-full" data-testid="shop-filter-toggle"><SlidersHorizontal className="w-4 h-4 mr-2" /> {t("common.filters")}</Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="font-display text-lg font-bold text-stone-900">{t("common.filters")}</div>
              <button onClick={() => setMobileOpen(false)} className="text-stone-400"><X className="w-5 h-5" /></button>
            </div>
            <FilterPanel {...filterProps} onApply={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-24"><FilterPanel {...filterProps} /></div>
        </aside>

        <div>
          {!loading && products.length === 0 ? (
            <div className="py-20 text-center">
              <div className="text-4xl mb-3">🔍</div>
              <div className="font-display text-lg font-semibold text-stone-900">No products found</div>
              <p className="text-stone-500 text-sm mt-1">Try adjusting your filters or search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {products.map((p) => (<ProductCard key={p.id} p={p} slug={slug} variant={cardVariant} />))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
